import { Menu, X } from "lucide-react";

function Linkedin({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.36V9h3.41v1.56h.05c.47-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 11.01-4.13 2.06 2.06 0 010 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z" />
    </svg>
  );
}
import { useEffect, useState } from "react";

const navLinks = [
  { label: "Work", href: "https://testedgrowth.com/#case-studies" },
  { label: "Services", href: "https://testedgrowth.com/#services" },
  { label: "Process", href: "https://testedgrowth.com/#process" },
  { label: "FAQ", href: "https://testedgrowth.com/#faq" },
];

export function Wordmark({ className = "" }: { className?: string }) {
  return (
    <span className={`font-bold tracking-tight text-white ${className}`}>
      Tested<span className="brand-grad-text">Growth</span>
      <span className="brand-grad-text">*</span>
    </span>
  );
}

export function SiteNav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-[hsl(var(--surface)/0.8)] backdrop-blur-md border-b border-[color:hsl(var(--border))]"
            : "bg-transparent"
        }`}
      >
        <div className="container-tg flex items-center justify-between h-16 md:h-20">
          <a
            href="https://testedgrowth.com"
            aria-label="Tested Growth home"
          >
            <Wordmark className="text-lg md:text-xl" />
          </a>

          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((l) => (
              <a
                key={l.label}
                href={l.href}
                className="text-sm text-[color:hsl(var(--muted-foreground))] hover:text-white transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a href="#audit" className="hidden md:inline-flex cta-primary cta-primary--sm">
              Free Audit
            </a>
            <button
              onClick={() => setOpen((v) => !v)}
              className="md:hidden p-2 -mr-2 text-white"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      {open && (
        <div className="fixed inset-0 z-40 bg-[color:hsl(var(--background))] md:hidden pt-20 fade-in">
          <nav className="container-tg flex flex-col gap-2 pt-8">
            {navLinks.map((l) => (
              <a
                key={l.label}
                href={l.href}
                className="py-4 text-2xl font-semibold border-b border-[color:hsl(var(--border))] text-white"
                onClick={() => setOpen(false)}
              >
                {l.label}
              </a>
            ))}
            <a
              href="#audit"
              onClick={() => setOpen(false)}
              className="mt-8 cta-primary"
            >
              Free Audit
            </a>
          </nav>
        </div>
      )}
    </>
  );
}

export function SiteFooter() {
  return (
    <footer className="border-t border-[color:hsl(var(--border))] bg-[color:hsl(var(--surface)/0.4)]">
      <div className="container-tg py-16 grid md:grid-cols-3 gap-10">
        <div>
          <Wordmark className="text-xl" />
          <p className="mt-3 text-[color:hsl(var(--muted-foreground))]">Growth you can prove.</p>
          <p className="mt-8 text-sm text-[color:hsl(var(--subtle))]">© 2026 Tested Growth</p>
        </div>

        <nav className="flex flex-col gap-3" aria-label="Footer">
          <p className="label-eyebrow mb-2">Site</p>
          {[
            { label: "Services", href: "https://testedgrowth.com/#services" },
            { label: "Process", href: "https://testedgrowth.com/#process" },
            { label: "FAQ", href: "https://testedgrowth.com/#faq" },
            { label: "Free Audit", href: "#audit" },
          ].map((l) => (
            <a
              key={l.label}
              href={l.href}
              className="text-[color:hsl(var(--muted-foreground))] hover:text-white transition-colors w-fit"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex flex-col gap-3">
          <p className="label-eyebrow mb-2">Contact</p>
          <a
            href="mailto:hello@testedgrowth.com"
            className="text-[color:hsl(var(--muted-foreground))] hover:text-white transition-colors w-fit"
          >
            hello@testedgrowth.com
          </a>
          <a
            href="https://www.linkedin.com/company/testedgrowth"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="LinkedIn"
            className="mt-2 w-10 h-10 rounded-full border border-[color:hsl(var(--border))] flex items-center justify-center text-[color:hsl(var(--muted-foreground))] hover:text-[color:hsl(var(--primary))] hover:border-[color:hsl(var(--primary))] transition-colors"
          >
            <Linkedin size={16} />
          </a>
        </div>
      </div>
    </footer>
  );
}
